import { Fragment } from 'react'
import { Icon } from '../../components/ui/Icon'
import { Spinner } from '../../components/ui/Spinner'
import type { RfqStatus } from './domain'

export interface RfqSubmissionStatusBannerProps {
  readonly status: RfqStatus
  /** Epoch ms captured at the moment `POST /rfq/{rfqId}/submit` succeeded. */
  readonly submittedAt: number
}

/**
 * UID-586 (`Fictiv Platform.dc.html` ~L4137-4151) — the Create-RFQ review
 * page's own in-page submission banner, shown INSIDE the "Parts in this
 * request" card (above the parts table), never as a navigation. Two
 * mutually-exclusive states, keyed directly off the polled `RfqStatus`:
 *
 *   - `requested`/`quoting` — the amber "RFQ submitted" banner, with its own
 *     compact 3-step tracker.
 *   - `quoted` — the green "Quote returned" banner.
 *
 * Renders nothing for every other status (`draft` never reaches this banner;
 * `accepted`/`declined`/`expired` are terminal states this page does not
 * represent — the user has by then moved on to the RFQ list/detail page).
 *
 * DELIBERATELY NOT `StatusStepper`/`RFQ_STEPS`/`stepState` (domain.ts) —
 * those already exist for the RFQ list/detail cards, but the design source
 * for THIS banner (`trackDotStyle`/`trackStepStyle`/`trackLineStyle`,
 * `dc.html:9712-9714`) draws a genuinely different 3-VALUE state, not the
 * detail page's binary completed/current/upcoming: `done` (navy) and `on`
 * (amber, `#B4690E`/`#8b5a00`) are SEPARATE flags there, not two names for
 * the same "reached" state. Reusing `stepState` would have painted "In
 * review by sourcing" the same navy as "Submitted" instead of amber, and
 * would size the connector lines by leftover flex space after three full
 * text labels — which, for these specific labels, leaves ~0px and renders
 * as no visible line at all (confirmed against the actual build).
 *
 * This banner shows ONLY while `requested`/`quoting`, and on that path the
 * three steps' states are FIXED, never computed from an index: "Submitted"
 * is always done, "In review by sourcing" is always the current (`on`,
 * amber) step, and "Quoted" is always upcoming — there is no fourth
 * sub-state to distinguish `requested` from `quoting` in this tracker (the
 * design's own `trackerStage` is 1 for both).
 */
export function RfqSubmissionStatusBanner({ status, submittedAt }: RfqSubmissionStatusBannerProps) {
  if (status === 'quoted') {
    return (
      <output className="rfq-submission-banner rfq-submission-banner--returned">
        <Icon name="check" className="rfq-submission-banner-icon" />
        <span>
          <b>Quote returned</b> — review pricing below. Editing requirements will send the part back
          for a new quote.
        </span>
      </output>
    )
  }

  if (status !== 'requested' && status !== 'quoting') {
    return null
  }

  return (
    <output className="rfq-submission-banner rfq-submission-banner--pending">
      <div className="rfq-submission-banner-row">
        <Spinner size="sm" className="rfq-submission-banner-spinner" aria-hidden="true" />
        <span>
          <b>RFQ submitted</b> — waiting on pricing from sourcing. You can keep editing parts and
          requirements below; changes will be included.
        </span>
      </div>
      {/* BUG FIX (confirmed against the actual build) — the connector line
       * must be a SIBLING `<li>` of the step `<li>`, never a child span
       * inside it: `StatusStepper.tsx` (this vertical's own detail-page
       * stepper) documents exactly this trap. A step `<li>` sizes itself to
       * fit its own content (dot + label), so a `flex: 1 1 auto` line
       * nested inside it has nothing real to grow into — the `<li>`'s
       * width is computed FROM its children, not handed down to them. As a
       * true sibling of the step `<li>`s under the SAME `<ol>`, the line
       * competes for the `<ol>`'s own (real, rendered) width instead. */}
      <ol className="rfq-submission-tracker" aria-label="RFQ status">
        {SUBMISSION_TRACKER_STEPS.map((step, index) => (
          <Fragment key={step.label}>
            <li className={`rfq-submission-tracker-step rfq-submission-tracker-step-${step.state}`}>
              <span className="rfq-submission-tracker-dot" aria-hidden="true" />
              <span className="rfq-submission-tracker-label">{step.label}</span>
            </li>
            {index < SUBMISSION_TRACKER_STEPS.length - 1 && (
              <li
                className={`rfq-submission-tracker-line rfq-submission-tracker-line-${
                  step.state === 'completed' ? 'done' : 'upcoming'
                }`}
                aria-hidden="true"
              />
            )}
          </Fragment>
        ))}
      </ol>
      <p className="rfq-submission-tracker-note">
        {submissionElapsedNote(submittedAt)} · custom quotes typically take 1–2 business days
      </p>
    </output>
  )
}

/** Design-literal labels + FIXED state for THIS banner's tracker
 * (`dc.html:4141-4145`/`9739-9741`) — see this file's header comment for
 * why these three states never vary while the pending banner is shown. */
const SUBMISSION_TRACKER_STEPS = [
  { label: 'Submitted', state: 'completed' as const },
  { label: 'In review by sourcing', state: 'current' as const },
  { label: 'Quoted', state: 'upcoming' as const },
]

const ONE_DAY_MS = 24 * 60 * 60 * 1000

/** Mirrors the design source's own tracker-note logic (`dc.html`'s
 * `trackerNote` computed property): "moments ago" for under a day, then a
 * whole-day count. No generic time-ago library — the design specifies
 * exactly these two forms and nothing finer-grained. */
function submissionElapsedNote(submittedAt: number): string {
  const daysElapsed = Math.max(0, Math.floor((Date.now() - submittedAt) / ONE_DAY_MS))
  if (daysElapsed < 1) {
    return 'Submitted moments ago'
  }
  return `Submitted ${daysElapsed} day${daysElapsed > 1 ? 's' : ''} ago`
}
